'use client';

import { Globe, Lock, Users } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { LockedFeatureOverlay } from '@/components/ui/locked-feature';
import { useState } from 'react';
import { toast } from 'sonner';

export type PostVisibility = 'public' | 'private' | 'private_explicit';

interface VisibilitySelectorProps {
  value: PostVisibility;
  onChange: (value: PostVisibility) => void;
  isLive?: boolean;
  disabled?: boolean;
}

export function VisibilitySelector({ value, onChange, isLive, disabled }: VisibilitySelectorProps) {
  // Check feature flags
  const canUseDomainOnly = useFeatureIsOn('visibility_domain_only');
  const canUseAlwaysPrivate = useFeatureIsOn('visibility_always_private');
  
  // Track clicks on locked features for analytics
  const handleLockedFeatureClick = (feature: string, requiredPlan: string) => {
    toast.info(`${feature} is available in the ${requiredPlan} plan`, {
      description: 'Upgrade to unlock this feature',
      action: {
        label: 'View Plans',
        onClick: () => {
          window.location.href = '/dashboard/settings/billing';
        },
      },
    });
    
    // Log for analytics
    console.log('[VisibilitySelector] Locked feature clicked:', { feature, requiredPlan });
  };

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">Post Visibility</Label>
      <RadioGroup
        value={value}
        onValueChange={(newValue) => {
          // Only change value if the feature is available
          if (newValue === 'private' && !canUseDomainOnly) {
            handleLockedFeatureClick('Domain Only visibility', 'Pro');
            return;
          }
          if (newValue === 'private_explicit' && !canUseAlwaysPrivate) {
            handleLockedFeatureClick('Always Private visibility', 'Pro');
            return;
          }
          onChange(newValue as PostVisibility);
        }}
        disabled={disabled}
        className="space-y-2"
      >
        <div className={cn(
          "flex items-start space-x-3 rounded-lg border p-3 transition-colors cursor-pointer",
          value === 'public' ? 'border-primary bg-primary/5' : 'border-muted hover:border-muted-foreground/50'
        )}>
          <RadioGroupItem value="public" id="public" className="mt-0.5" />
          <Label htmlFor="public" className="flex-1 cursor-pointer">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="font-medium">Public</span>
              {isLive && <span className="text-xs text-muted-foreground">(Default for published posts)</span>}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Anyone can discover and view this post. It will appear in public search results and on explore pages.
            </p>
          </Label>
        </div>

        <div className="relative">
          <div 
            className={cn(
              "flex items-start space-x-3 rounded-lg border p-3 transition-colors",
              canUseDomainOnly ? "cursor-pointer" : "cursor-not-allowed opacity-75",
              value === 'private' ? 'border-primary bg-primary/5' : 'border-muted hover:border-muted-foreground/50'
            )}
          >
            <RadioGroupItem 
              value="private" 
              id="private" 
              className="mt-0.5" 
              disabled={!canUseDomainOnly || disabled}
            />
            <Label htmlFor="private" className="flex-1 cursor-pointer">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <span className="font-medium">Domain Only</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Only members of your domain can view this post. Perfect for internal content.
              </p>
            </Label>
          </div>
          {!canUseDomainOnly && <LockedFeatureOverlay plan="pro" />}
        </div>

        <div className="relative">
          <div 
            className={cn(
              "flex items-start space-x-3 rounded-lg border p-3 transition-colors",
              canUseAlwaysPrivate ? "cursor-pointer" : "cursor-not-allowed opacity-75",
              value === 'private_explicit' ? 'border-primary bg-primary/5' : 'border-muted hover:border-muted-foreground/50'
            )}
          >
            <RadioGroupItem 
              value="private_explicit" 
              id="private_explicit" 
              className="mt-0.5" 
              disabled={!canUseAlwaysPrivate || disabled}
            />
            <Label htmlFor="private_explicit" className="flex-1 cursor-pointer">
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <span className="font-medium">Always Private</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Keep this post private even when published. It will never appear in public search results.
              </p>
            </Label>
          </div>
          {!canUseAlwaysPrivate && <LockedFeatureOverlay plan="pro" />}
        </div>
      </RadioGroup>
    </div>
  );
}